import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import {
	CurrencyType,
	ValuationInputType,
	ValuationNumberFormat,
} from '@dealstack/shared/lib/generated';
import { DateTime } from 'luxon';
import { z } from 'zod';
import type { CreateInputAdjustmentTableForm, ValuationDetailsForm } from './constants.types';

export const VALUATION_HEADER_CURRENCIES = Object.values(CurrencyType);

// Regex to check if the string is a valid decimal number, say "3.4" or "-12.345".
const decimalPattern = /^[+-]?((\d+(\.(?!\.))?|(?=\.))(\.\d+)?)$/;
const currencyAmountCheck = ({
	value,
	currentNumberFactor,
	integerPartLength = 10,
	decimalPartLength = 5,
	allowNegative = true,
}: {
	value: string;
	currentNumberFactor: number;
	integerPartLength?: number;
	decimalPartLength?: number;
	allowNegative?: boolean;
}) => {
	if (!decimalPattern.test(value)) {
		return false;
	}
	const decimal = new HighPrecisionDecimal(value).mul(currentNumberFactor);
	if (!allowNegative && decimal.lt(0)) {
		return false;
	}
	const [integerPart, decimalPart] = decimal.toFixed(decimal.dp()).replace('-', '').split('.');
	return (
		integerPart.length <= integerPartLength &&
		(decimalPart === undefined || decimalPart.length <= decimalPartLength)
	);
};

export const createInputAdjustmentTableFormShape = (currentNumberFactor: number) =>
	z.object({
		valuationInput: z.object({
			inputId: z.string(),
			inputType: z.nativeEnum(ValuationInputType),
			value: z.object({
				currency: z.nativeEnum(CurrencyType),
				currencyAmount: z.string().refine((value) =>
					currencyAmountCheck({
						value,
						currentNumberFactor,
						allowNegative: false,
						integerPartLength: 15,
					})
				),
			}),
		}),
		cash: z.array(
			z.object({
				adjustmentId: z.string().optional(),
				tempId: z.string().optional(),
				name: z.string(),
				value: z.object({
					currency: z.nativeEnum(CurrencyType),
					currencyAmount: z
						.string()
						.refine((value) => currencyAmountCheck({ value, currentNumberFactor })),
				}),
			})
		),
		workingCapital: z.array(
			z.object({
				adjustmentId: z.string().optional(),
				tempId: z.string().optional(),
				name: z.string(),
				value: z.object({
					currency: z.nativeEnum(CurrencyType),
					currencyAmount: z
						.string()
						.refine((value) => currencyAmountCheck({ value, currentNumberFactor })),
				}),
			})
		),
	});
export const defaultInputAdjustmentTableFormValues: CreateInputAdjustmentTableForm = {
	valuationInput: {
		inputId: '',
		inputType: ValuationInputType.EQUITY_VALUE,
		value: {
			currency: CurrencyType.EUR,
			currencyAmount: '0',
		},
	},
	cash: [],
	workingCapital: [],
};

export const createValuationDetailsFormShape = z.object({
	valuationTitle: z.string().trim().min(1),
	valuationDate: z.string(),
	valuationMultiples: z.nativeEnum(ValuationNumberFormat),
	valuationTags: z.array(z.string()),
	valuationDefaultCurrency: z.nativeEnum(CurrencyType),
});
export const defaultValuationDetailsFormValues: ValuationDetailsForm = {
	valuationTitle: '',
	valuationDate: DateTime.now().toFormat('yyyy-MM-dd'),
	valuationMultiples: ValuationNumberFormat.UNFORMATTED,
	valuationTags: [],
	valuationDefaultCurrency: CurrencyType.EUR,
};

export const fxRatesFormSchema = z.object({
	rates: z.record(
		z.string(),
		z.object({
			history: z.number().nullish(),
			custom: z
				.string()
				.regex(/^\+?((\d{1,10}(\.(?!\.))?|(?=\.))(\.\d{1,5})?)$/)
				.nullish(),
		})
	),
});
